/* eslint-disable no-unused-vars */

import React, {
  useState, useEffect, useRef, useMemo
} from "react";
import {
  yReview, clipStar, reviewUser, yReviewsInner,
  yReviews, reviewsWrapper, reviewsHeader,
  reviewsTitle, starWrapper,
  reviewsScore, rHeader, rUser, rVerified, rTitle,
  rContent, rDate, rUserVerified, rPagination, rPages,
  activePage, pLeft, pRight, reviewTop, rTopLeft, rTopRight,
  rTopStars,
  rImage, rImagesContainer, activeStars
} from "./style.mod.scss";
import Loader from "components/loader";
import { Container } from "react-bootstrap";
import ImageViewer from "react-simple-image-viewer";
import { Star, StarFill, } from "react-bootstrap-icons";
import VerifiedIcon from "assets/svgs/verified.svg";
import Dropdown from "./dropdown";
import locale from "locales";
import {
  getChannelReviews,
  getMergedReviewsForCluster
} from "../../data/trusted-reviews";

const Reviews = ({ shopifyId, data, variants, isService, cluster }) => {
  const fallbackShopifyId = data?.shopifyId;
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({});
  const [reviews, setReviews] = useState([]);
  const [page, setPage] = useState(1);
  const [previewImages, setPreviewImages] = useState([]);
  const [previewImageIndex, setPreviewImageIndex] = useState(0);
  const [sort, setSort] = useState(["date", "desc"]);
  const [stars, setStars] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ref = useRef();

  const round = (avg) => avg && Math.round(avg);
  const prec = (avg) => avg && Number.parseFloat(avg).toPrecision(2);

  const reviewsPerPage = 5;

  const indexOfLastReview = currentPage * reviewsPerPage;
  const indexOfFirstReview = indexOfLastReview - reviewsPerPage;

  const formatDate = (sDate) => {
    const date = new Date(sDate);
    let ye = new Intl.DateTimeFormat("en", { year: "numeric" }).format(date);
    let mo = new Intl.DateTimeFormat("en", { month: "numeric" }).format(date);
    let da = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(date);
    return `${da}.${mo}.${ye}`;
  };

  const groupReviewsByRating = (reviews) => {
    if (!reviews || reviews.length === 0) return {
      average: 0, grouped: {}
    };

    const grouped = {};
    let totalRating = 0;

    reviews.forEach(review => {
      const rating = review.score;
      if (!grouped[rating]) {
        grouped[rating] = 0;
      }

      grouped[rating] += 1;
      totalRating += rating;
    });

    const average = totalRating / reviews.length;

    return {
      grouped, average: Number(average.toFixed(1))
    };
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
      window.scrollTo({
        top: ref.current.offsetTop,
        behavior: "smooth"
      });
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
      window.scrollTo({
        top: ref.current.offsetTop,
        behavior: "smooth"
      });

    }
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
    window.scrollTo({
      top: ref.current.offsetTop,
      behavior: "smooth"
    });
  };

  useEffect(() => {
    if (variants) {
      getMergedReviewsForCluster(variants);
    }
  }, [variants]);

  useEffect(() => {
    if (reviews.length > 0) {
      const { grouped, average } = groupReviewsByRating(reviews);
      setSummary({
        total_review: reviews?.length,
        average_score: average,
        star_distribution: grouped
      });
    }
  }, [reviews]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      let reviewsData;
      if (variants?.length) {
        reviewsData = await getMergedReviewsForCluster(variants);
      } else if (isService) {
        reviewsData = await getMergedReviewsForCluster(cluster);
      } else {
        reviewsData = await getChannelReviews();
      }

      const clusters = variants?.map(variant => variant.cluster).filter(Boolean) || [];
      if (clusters.length > 0) {
        reviewsData = reviewsData.filter(review => review.cluster && clusters.includes(review.cluster));
      }
      const formattedReviews = reviewsData.map((review) => ({
        id: review.id,
        user: { display_name: review?.customer?.fullName || "" },
        score: review.rating,
        title: review.title?.trim() || "",
        content: review.comment?.trim() || "",
        created_at: review.submittedAt,
        verified_buyer: review.verificationType === "VERIFIED",
        images_data: review.product?.imageUrl
          ? [{
            original_url: review.product.imageUrl, thumb_url: review.product.imageUrl
          }]
          : [],
      }));
      setReviews(formattedReviews);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (reviews.length === 0) {
      fetchReviews();
    }
  }, []);

  const sortedAndFilteredReviews = useMemo(() => {
    let filtered = stars
      ? reviews.filter((review) => review.score === parseFloat(stars))
      : reviews;

    let sorted = [...filtered];
    const [sortBy, order] = sort;

    if (sortBy === "with_pictures") {
      sorted = sorted
        .filter((review) => review.images_data.length > 0)
        .sort((a, b) => {
          return new Date(b.created_at) - new Date(a.created_at);
        });
    } else if (sortBy === "date") {
      sorted.sort((a, b) =>
        order === "desc"
          ? new Date(b.created_at) - new Date(a.created_at)
          : new Date(a.created_at) - new Date(b.created_at)
      );
    } else if (sortBy === "rating") {
      sorted.sort((a, b) => {
        if (a.score === b.score) {
          return new Date(b.created_at) - new Date(a.created_at);
        }
        return order === "asc" ? a.score - b.score : b.score - a.score;
      });
    }

    return sorted;
  }, [reviews, sort, stars]);

  const handleSortChange = (selectedOption) => {
    setSort(selectedOption);
  };

  const handleFilterByRating = (rating) => {
    if (rating === stars) {
      setStars(null);
    } else {
      setStars(rating);
    }
  };

  const currentReviews = useMemo(() => {
    return sortedAndFilteredReviews.slice(indexOfFirstReview, indexOfLastReview);
  }, [sortedAndFilteredReviews, indexOfFirstReview, indexOfLastReview, currentPage]);

  const totalPages = useMemo(() => {
    return Math.ceil(sortedAndFilteredReviews.length / reviewsPerPage);
  }, [sortedAndFilteredReviews]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortedAndFilteredReviews]);

  const Stars = ({ score }) => {
    if (!score) return <></>;

    return Array(round(score)).fill().map(() => (
      <div key={Math.random()} className={clipStar}><StarFill /></div>
    ));
  };

  const StarsWithEmpty = ({ score, onClick, className }) => {
    const rounded = round(score);
    const emptySt = 5 - rounded;

    const filled = rounded > 0 && Array(rounded).fill().map(() => (
      <div key={Math.random()} className={clipStar}><StarFill /></div>
    ));

    const empty = emptySt > 0 && Array(emptySt).fill().map(() => (
      <div key={Math.random()} className={clipStar}><Star /></div>
    ));

    return (
      <div className={className} onClick={onClick}>
        {filled}
        {empty}
      </div>
    );
  };


  const Pages = () => {
    const getPagination = () => {
      const pages = new Set();

      pages.add(1);
      pages.add(totalPages);
      pages.add(currentPage);
      if (currentPage > 1) pages.add(currentPage - 1);
      if (currentPage < totalPages) pages.add(currentPage + 1);
      const sortedPages = [...pages].sort((a, b) => a - b);

      const displayPages = [];
      for (let i = 0; i < sortedPages.length; i++) {
        if (i > 0 && sortedPages[i] - sortedPages[i - 1] > 1) {
          displayPages.push("...");
        }
        displayPages.push(sortedPages[i]);
      }

      return displayPages;
    };

    return (
      <div className={rPagination}>
        <button className={pLeft} onClick={prevPage} disabled={currentPage === 1}>
          <i></i>
        </button>

        <span className={rPages}>
          {getPagination().map((page, index) => (
            <a
              key={index}
              className={page === currentPage ? activePage : ""}
              onClick={() => typeof page === "number" && handlePageClick(page)}
              disabled={page === "..."}
            >
              {page}
            </a>
          ))}
        </span>


        <button className={pRight} onClick={nextPage} disabled={currentPage === totalPages}>
          <i></i>
        </button>
      </div>
    );
  };

  const Review = ({ data }) => {
    const decodeHTMLEntities = (text) => {
      let textArea = document.createElement("textarea");
      textArea.innerHTML = text;
      return textArea.value;
    };

    return (
      <div className={yReview}>
        <div className={reviewUser}>
          <div className={rHeader}>
            <h5 className={rUser}>
              {data.user.display_name}
              <span className={rUserVerified} />
            </h5>
            <span><Stars score={data.score} /></span>
          </div>
          <h3 className={rTitle}>{decodeHTMLEntities(data.title)}</h3>
          {
            data.verified_buyer &&
            <h5 className={rVerified}>
              <VerifiedIcon />
              {locale.pages.reviews.verified}
            </h5>
          }
        </div>
        <div className={rContent}>{decodeHTMLEntities(data.content)}
          {
            data.images_data &&
            <div className={rImagesContainer}>
              {
                data.images_data && data.images_data.map((image, i) => (
                  <img
                    onClick={() => {
                      setPreviewImages(data.images_data.map((img) => img.original_url));
                      setPreviewImageIndex(i);
                    }}
                    className={rImage}
                    key={i}
                    src={image.thumb_url}
                  />
                ))
              }
            </div>
          }
        </div>


        <h5 className={rDate}>{formatDate(data.created_at)}</h5>
      </div>
    );
  };

  if (loading) return <Loader />;
  if (summary?.total_review === 0) return <></>;
  const shouldHideReviews = typeof window !== "undefined" && window.location.href.includes("foodboom");
  if (shouldHideReviews) return <></>;

  const ReviewTop = ({ hide }) => {
    if (hide) return (<></>);

    const distribution = summary?.star_distribution;
    if (!distribution) return <></>;

    return (
      <div className={reviewTop}>
        <div className={rTopLeft}>
          <div className={rTopStars}>
            <Stars score={summary?.average_score} />
            {
              summary?.total_review &&
              <h4>{reviews.length} {locale.pages.reviews.reviews}</h4>
            }

          </div>

        </div>
        <div className={rTopRight}>
          {
            Object.keys(distribution).sort((el1, el2) => el2 - el1).map((_key) => (
              <div key={_key} data-score={_key} className={rTopStars}>
                <StarsWithEmpty className={stars === _key ? activeStars : ""} onClick={() => handleFilterByRating(_key)} score={_key} />
                <h5>({distribution[_key]})</h5>

              </div>
            ))
          }
        </div>
      </div>
    );
  };

  return (
    <div ref={ref} className={yReviews} id="y-product-reviews">
      <Container>
        <div className={yReviewsInner}>
          <div className={reviewsHeader}>
            <h1 className={reviewsTitle}>{locale.pages.reviews.happyCustomers}</h1>
            <div className={reviewsScore}>
              <div className={starWrapper}>
                <div>
                  <Stars score={summary?.average_score} />
                </div>
                {
                  summary?.total_review &&
                  <h5>({reviews?.length} {locale.pages.reviews.reviews})</h5>
                }
              </div>
              <h3>{prec(summary?.average_score)}</h3>
            </div>
            <Dropdown
              defaultIndex={0}
              options={[
                [["date", "desc"], locale.pages.reviews.newest],
                [["rating", "desc"], locale.pages.reviews.highestRating],
                [["rating", "asc"], locale.pages.reviews.lowestRating],
                (shopifyId || fallbackShopifyId) && [["with_pictures", "true"], locale.pages.reviews.withPictures],
              ]}
              onChange={handleSortChange}
            />
          </div>
          <div id="reviews-wrapper" className={reviewsWrapper}>
            <ReviewTop hide={false} />
            <h2 className={reviewsTitle}>{locale.pages.reviews.reviews} ({reviews.length})</h2>
            <hr />
            {
              currentReviews?.length === 0 && (
                <span>{locale.pages.reviews.noReviews}</span>
              )
            }
            {
              currentReviews?.map((_review) => <Review key={_review.id} data={_review} />)
            }
          </div>
          <Pages pagination={page} length={reviews?.length} />
        </div>
        {
          previewImages?.length !== 0 && (
            <ImageViewer
              src={previewImages}
              currentIndex={previewImageIndex}
              disableScroll={false}
              closeOnClickOutside={true}
              onClose={() => setPreviewImages([])}
            />
          )
        }
      </Container>
    </div>
  );
};

export default Reviews;
